<template>
  <div class="file-upload-container">
    <label :for="id" class="file-upload-label">{{ label }}</label>
    <div 
      class="file-upload-info-box"
      v-b-tooltip.hover.html
      :title="fileFormatsTooltip"
    >
      <feather-icon icon="InfoIcon" size="14" class="info-icon" />
      <small class="file-upload-info-text">
        <strong>{{ tf('actions.file_upload.accepted_types_label', 'Tipos aceptados:') }}</strong> {{ tf('actions.file_upload.accepted_types_value', 'Imágenes, PDF, Word, Excel.') }}
        <strong>{{ tf('actions.file_upload.max_label', 'Máximo:') }}</strong> {{ tf('actions.file_upload.max_per_document', '5 MB por documento') }} • {{ tf('actions.file_upload.max_documents_total', '5 documentos en total') }}
      </small>
    </div>
    <div class="upload-button-container">
      <b-button
        variant="primary"
        size="sm"
        :disabled="disabled || uploading || deleting"
        class="upload-button"
      >
        <feather-icon icon="UploadIcon" size="18" />
        <input
          :id="id"
          type="file"
          @change="handleFileChange"
          multiple
          :disabled="disabled || uploading || deleting"
          class="file-input"
        />
      </b-button>
      <div v-if="uploading" class="uploading-indicator">
        <b-spinner small variant="primary" />
        <span class="uploading-text">{{ tf('actions.file_upload.uploading', 'Subiendo archivo(s)...') }}</span>
      </div>
      <div v-if="deleting" class="uploading-indicator">
        <b-spinner small variant="primary" />
        <span class="uploading-text">{{ tf('actions.file_upload.deleting', 'Eliminando archivo...') }}</span>
      </div>
    </div>

    <div class="previews" v-if="existingPreviews.length || previews.length">
      <!-- EXISTING FILES -->
      <div
        v-for="(file, index) in existingPreviews"
        :key="'existing-' + index"
        class="preview-container"
      >
        <div class="preview-content">
          <div
            class="preview-clickable"
            @click="openFile(file.url)"
          >
            <!-- image, thumbnail -->
            <img
              v-if="isImageFile(file)"
              :src="file.url"
              class="preview-image"
            />

            <!-- No image,  icon + extension -->
            <div v-else class="file-icon-box">
              <feather-icon
                :icon="iconForFile(file)"
                class="file-icon"
                size="24"
              />
              <span class="file-ext">{{ fileExtension(file) }}</span>
            </div>
          </div>

          <small class="file-name" :title="file.name">{{ file.name }}</small>

          <button
            class="remove-button"
            @click="removeExistingFile(index)"
            :disabled="disabled || uploading || deleting"
          >
            &#10005;
          </button>
        </div>
      </div>

      <!-- NEW (LOCAL) FILES -->
      <div
        v-for="(preview, index) in previews"
        :key="'new-' + index"
        class="preview-container"
      >
        <div class="preview-content">
          <div
            class="preview-clickable"
            @click="openFile(preview.url)"
          >
            <img
              v-if="isImageFile(preview)"
              :src="preview.url"
              class="preview-image"
            />
            <div v-else class="file-icon-box">
              <feather-icon
                :icon="iconForFile(preview)"
                class="file-icon"
                size="24"
              />
              <span class="file-ext">{{ fileExtension(preview) }}</span>
            </div>
          </div>

          <small class="file-name" :title="preview.name">{{ preview.name }}</small>

          <button
            class="remove-button"
            @click="removeFile(index)"
            :disabled="disabled || uploading || deleting"
          >
            &#10005;
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    id: String,
    name: String,
    label: String,
    multiple: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
    uploading: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    onUpdateFiles: Function,
    // [{ url, name, (optional) type }]
    existingFiles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      files: [],
      previews: [],
      existingPreviews: this.existingFiles
        .filter(f => f.url && String(f.url).trim() !== "")
        .map(f => ({
          url: f.url,
          name: f.name,
          type: f.type || null,
        })),
      maxFileSizeMB: 30,
      maxFilesCount: 5,
    };
  },
  computed: {
    fileFormatsTooltip() {
      return this.tf('actions.file_upload.tooltip', 'Imágenes: JPG, JPEG, PNG, GIF, BMP, WEBP, SVG<br>Documentos: PDF<br>Word: DOC, DOCX, ODT, RTF<br>Excel: XLS, XLSX, CSV, ODS')
    }
  },
  watch: {
    existingFiles: {
      handler(newVal = []) {
        this.existingPreviews = (newVal || [])
          .filter(f => f.url && String(f.url).trim() !== "")
          .map(f => ({
            url: f.url,
            name: f.name,
            type: f.type || null,
          }));
      },
      deep: true,
    },
    uploading(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.files = [];
        this.previews = [];
      }
    },
  },
  methods: {
    tf(key, fallback = '') {
      try {
        const out = this.$t ? this.$t(key) : key
        return typeof out === 'string' && out === key ? fallback : out
      } catch (e) {
        return fallback
      }
    },
    handleFileChange(event) {
      if (this.disabled) return;
      const newFiles = Array.from(event.target.files || []);

      const currentTotalFiles = this.files.length + this.existingPreviews.length;
      const maxAllowed = this.maxFilesCount;
      
      if (currentTotalFiles >= maxAllowed) {
        if (this.$toast) {
          this.$toast.error(this.$t('actions.file_upload.max_documents_error', { n: maxAllowed }) || `Máximo ${maxAllowed} documentos permitidos`);
        }
        event.target.value = "";
        return;
      }

      const maxSizeBytes = this.maxFileSizeMB * 1024 * 1024;
      const validFiles = [];
      const errors = [];

      newFiles.forEach(file => {
        if (file.size > maxSizeBytes) {
          errors.push(this.$t('actions.file_upload.max_size_error', { name: file.name, n: this.maxFileSizeMB }) || `El archivo ${file.name} excede el tamaño máximo de ${this.maxFileSizeMB} MB`);
          return;
        }

        const willExceedLimit = currentTotalFiles + validFiles.length >= maxAllowed;
        if (willExceedLimit) {
          errors.push(this.$t('actions.file_upload.max_documents_error', { n: maxAllowed }) || `Máximo ${maxAllowed} documentos permitidos`);
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        if (this.$toast) {
          errors.forEach(error => this.$toast.error(error));
        }
        event.target.value = "";
      }

      validFiles.forEach(file => {
        this.files.push(file);
        this.previews.push({
          url: URL.createObjectURL(file),
          name: file.name,
          type: file.type || null,
        });
      });

      if (validFiles.length > 0) {
        this.emitFiles();
      }

      event.target.value = "";
    },

    removeFile(index) {
      if (this.disabled) return;
      this.files.splice(index, 1);
      this.previews.splice(index, 1);
      this.emitFiles();
    },

    removeExistingFile(index) {
      if (this.disabled) return;
      this.existingPreviews.splice(index, 1);
      this.emitFiles();
    },

    openFile(url) {
      if (!url) return;
      window.open(url, "_blank");
    },

    //helpers icon type
    fileExtension(file) {
      let name = file?.name || "";
      if (!name && file?.url) {
        const cleanUrl = file.url.split("?")[0];
        name = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
      }
      const dot = name.lastIndexOf(".");
      if (dot === -1) return "";
      return name.substring(dot + 1).toUpperCase();
    },

    isImageFile(file) {
      const type = (file?.type || "").toLowerCase();
      if (type.startsWith("image/")) return true;

      const ext = this.fileExtension(file).toLowerCase();
      const imgExts = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
      return imgExts.includes(ext);
    },

    iconForFile(file) {
      const type = (file?.type || "").toLowerCase();
      const ext = this.fileExtension(file).toLowerCase();

      if (type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext)) {
        return "ImageIcon";
      }

      if (type.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a"].includes(ext)) {
        return "MusicIcon";
      }

      if (type.startsWith("video/") || ["mp4", "mov", "mkv", "avi", "webm"].includes(ext)) {
        return "VideoIcon";
      }

      if (ext === "pdf") {
        return "FileTextIcon";
      }

      if (["doc", "docx", "odt", "rtf"].includes(ext)) {
        return "FileTextIcon";
      }

      if (["xls", "xlsx", "csv", "ods"].includes(ext)) {
        return "FileIcon";
      }

      // generic
      return "FileIcon";
    },

    emitFiles() {
      const existingFiles = this.existingPreviews.map(file => ({
        url: file.url,
        name: file.name,
        raw: null,
      }));

      const newFiles = this.files.map(file => ({
        url: "",
        name: file.name,
        raw: file,
      }));

      if (this.onUpdateFiles) {
        this.onUpdateFiles({
          id: this.id,
          files: [...existingFiles, ...newFiles],
          label: this.name || this.label,
          rawFiles: this.files,
        });
      }
    },
  },
};
</script>

<style>
.file-upload-container {
  margin-top: 10px;
}

.file-upload-label {
  display: block;
  line-height: 1.5;
  font-size: 0.9em;
  font-weight: bolder;
  margin-bottom: 5px;
}

.file-upload-info-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  margin-bottom: 8px;
}

.info-icon {
  color: #0284c7;
  flex-shrink: 0;
  margin-top: 2px;
}

.file-upload-info-text {
  color: #0c4a6e;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.file-upload-info-text strong {
  color: #075985;
  font-weight: 600;
}

.upload-button-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.uploading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
}

.uploading-text {
  font-size: 0.875rem;
  color: var(--primary);
}

.upload-button {
  position: relative;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
}

.previews {
  display: flex;
  flex-wrap: wrap;
  margin-top: 10px;
}

.preview-container {
  position: relative;
  margin-right: 10px;
  margin-bottom: 10px;
}

.preview-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.preview-clickable {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-image {
  max-width: 100px;
  max-height: 100px;
  margin-bottom: 5px;
  object-fit: cover;
}

/* Caja para archivos no-imagen */
.file-icon-box {
  width: 100px;
  height: 100px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.file-icon {
  margin-bottom: 4px;
}

.file-ext {
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
}

.file-name {
  max-width: 110px;
  font-size: 11px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-button {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: red;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 14px;
  padding: 0;
}

.remove-button:disabled {
  background-color: gray;
  cursor: not-allowed;
}
</style>
